import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Tab,
  Tabs,
  Alert,
  useTheme,
  alpha
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from "recharts";
import { analyticsService } from "../api/services";

const AnalyticsDashboard = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await analyticsService.getDashboardData();
      setData(response.data);
    } catch (err) {
      console.error("Error fetching analytics:", err);
      setError("No se pudieron cargar los datos analíticos.");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Panel de Analíticas
      </Typography>

      {/* Métricas Clave */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ 
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.text.primary
          }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Vistas Totales
              </Typography>
              <Typography variant="h3" fontWeight="bold">
                {data?.summary?.total_views || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ 
            bgcolor: alpha(theme.palette.success.main, 0.1),
            color: theme.palette.text.primary
          }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Ingresos Estimados
              </Typography>
              <Typography variant="h3" fontWeight="bold" sx={{ color: theme.palette.success.main }}>
                ${data?.summary?.total_revenue?.toFixed(2) || "0.00"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ 
            bgcolor: alpha(theme.palette.warning.main, 0.1),
            color: theme.palette.text.primary
          }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Clicks en Publicidad
              </Typography>
              <Typography variant="h3" fontWeight="bold">
                {data?.summary?.total_clicks || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pestañas */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Tráfico" />
          <Tab label="Contenido Top" />
          <Tab label="Monetización (AdSense)" />
        </Tabs>
      </Box>

      {/* Tab 0: Tráfico */}
      {tabValue === 0 && (
        <Card sx={{ p: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Visitas e Ingresos (Últimos 30 días)
            </Typography>
            <Box sx={{ height: 400, width: '100%' }}>
              <ResponsiveContainer>
                <LineChart data={data?.chart_data || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="views" stroke="#8884d8" name="Vistas" activeDot={{ r: 8 }} />
                  <Line yAxisId="left" type="monotone" dataKey="visitors" stroke="#82ca9d" name="Visitantes Únicos" />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Tab 1: Contenido Top */}
      {tabValue === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Artículos Más Populares
            </Typography>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Título</TableCell>
                    <TableCell align="right">Vistas</TableCell>
                    <TableCell align="right">Ingresos Generados</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.top_posts?.map((post, index) => (
                    <TableRow key={index}>
                      <TableCell component="th" scope="row">
                        {post.title}
                      </TableCell>
                      <TableCell align="right">{post.views}</TableCell>
                      <TableCell align="right">${post.revenue?.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  {(!data?.top_posts || data.top_posts.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">No hay datos disponibles</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Tab 2: Monetización (Placeholder) */}
      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 3 }}>
              La integración con Google AdSense está configurada. Aquí verás los datos detallados cuando se active la monetización.
            </Alert>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Rendimiento de Anuncios</Typography>
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography color="textSecondary">Gráfico de RPM y CTR (Próximamente)</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Ingresos por Dispositivo</Typography>
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography color="textSecondary">Gráfico Circular (Próximamente)</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default AnalyticsDashboard;
